import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import {
  PRODUCT_CONDITIONS,
  ALLOWED_CURRENCIES,
  DIMENSION_UNIT,
  WEIGHT_UNIT,
} from "../constants/productAddingConstants";

import "./edit-product.css";

import {
  emptyFormForProductRespose,
  createEmptyVariant,
} from "../pages/commonForms";

const PRODUCT_API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const IMAGE_BASE = "https://auth.afrogebeya.com/uploads/products/";

export default function SellerEditProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingSub, setLoadingSub] = useState(false);
  const [product, setProduct] = useState(null);

  const [form, setForm] = useState(emptyFormForProductRespose);

  const [attributesMeta, setAttributesMeta] = useState({
    productAttributes: [],
    variantAttributes: [],
  });

  const [productAttributesValues, setProductAttributesValues] = useState({});

  const [variants, setVariants] = useState([createEmptyVariant()]);

  const [existingImages, setExistingImages] = useState([]);
  const [mainImageId, setMainImageId] = useState(null);
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const [removedVariantIds, setRemovedVariantIds] = useState([]);

  const [newImages, setNewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  const [stock, setStock] = useState({
    sellableQuantity: 0,
  });

  // ===============================
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const productRes = await fetch(
          `${PRODUCT_API_BASE}/products/seller/${id}`,
        );

        if (!productRes.ok) {
          throw new Error("Failed to fetch product");
        }

        const productData = await productRes.json();

        setProduct(productData);

        /* ===============================
         PRODUCT FORM
      =============================== */
        setForm({
          id: productData.id || null,
          productName: productData.productName || "",
          description: productData.description || "",
          brand: productData.brand || "",
          modelNumber: productData.modelNumber || "",

          categoryId: productData.categoryId ?? null,
          subCategoryId: productData.subCategoryId ?? null,

          currency: productData.currency || "ETB",
          productCondition: productData.productCondition || "NEW",

          dimensionUnit: productData.dimensionUnit || "cm",
          weightUnit: productData.weightUnit || "kg",

          returnPolicy: productData.returnPolicy || "",
          warrantyPeriod: productData.warrantyPeriod || "",
        });

        /* ===============================
         LOAD SUBCATEGORIES
      =============================== */

        if (productData.categoryId) {
          await loadSubCategories(productData.categoryId);
        }

        /* LOAD ATTRIBUTE META FIRST */
        let meta = {
          productAttributes: [],
          variantAttributes: [],
        };
        /* ATTRIBUTE META */
        if (productData.subCategoryId) {
          meta = await loadAttributes(productData.subCategoryId);
        }

        /* ===============================
         PRODUCT ATTRIBUTES
      =============================== */
        const rawAttributes = productData.attributes || {};

        const normalizedProductAttributes = {};
        meta.productAttributes.forEach((attr) => {
          normalizedProductAttributes[attr.code] =
            rawAttributes[attr.code] ?? rawAttributes[attr.name] ?? "";
        });

        setProductAttributesValues(normalizedProductAttributes);

        /* ===============================
         VARIANTS
      =============================== */

        const mappedVariants =
          productData.variants?.length > 0
            ? productData.variants.map((variant) => {
                const normalized = {};

                Object.entries(variant.attributes || {}).forEach(
                  ([key, value]) => {
                    const found = meta.variantAttributes.find(
                      (attr) =>
                        attr.name.toLowerCase() === key.toLowerCase() ||
                        attr.code.toLowerCase() === key.toLowerCase(),
                    );

                    if (found) {
                      normalized[found.code] = value;
                    }
                  },
                );

                return {
                  id: variant.id,
                  sku: variant.sku || "",
                  price: variant.price ?? "",
                  availableQuantity: variant.availableQuantity ?? "",
                  weight: variant.weight ?? "",
                  productCondition: variant.productCondition || "NEW",
                  productStatus: variant.productStatus || "ACTIVE",
                  isDefault: variant.isDefault || false,
                  attributes: normalized,
                  existingImages: variant.images || [],
                  newImages: [],
                  previews: [],
                  removedImageIds: [],
                };
              })
            : [createEmptyVariant()];

        setVariants(mappedVariants);
        /* ===============================
         PRODUCT IMAGES
      =============================== */
        setExistingImages(productData.images || []);
        setMainImageId(productData.mainImage?.id || null);
      } catch (err) {
        console.error(err);

        setError("Failed to load product data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    if (!product || !attributesMeta.productAttributes.length) return;

    const raw = product.attributes || {};
    const mapped = {};

    attributesMeta.productAttributes.forEach((attr) => {
      mapped[attr.code] = raw[attr.code] ?? raw[attr.name] ?? "";
    });

    setProductAttributesValues(mapped);
  }, [product, attributesMeta]);

  useEffect(() => {
    return () => {
      previewImages.forEach(URL.revokeObjectURL);
    };
  }, []);

  const allVariantPreviews = variants.flatMap((v) => v.previews || []);

  useEffect(() => {
    return () => {
      allVariantPreviews.forEach(URL.revokeObjectURL);
    };
  }, []);

  const loadSubCategories = async (categoryId) => {
    const res = await fetch(
      `${PRODUCT_API_BASE}/products/subcategories/${categoryId}`,
    );
    const data = await res.json();

    setSubCategories(Array.isArray(data) ? data : data.content || []);
  };

  const loadAttributes = async (subCategoryId) => {
    const res = await fetch(
      `${PRODUCT_API_BASE}/products/subcategories/${subCategoryId}/attributes`,
    );

    const data = await res.json();

    setAttributesMeta({
      productAttributes: data.productAttributes || [],
      variantAttributes: data.variantAttributes || [],
    });
    return data;
  };

  useEffect(() => {
    fetch(`${PRODUCT_API_BASE}/products/categories`)
      .then((res) => res.json())
      .then((data) => {
        setCategories(data.content || []);
      })
      .catch(console.error);
  }, []);

  /* ===============================
   GENERIC FORM CHANGE
================================= */
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "number"
          ? value === ""
            ? ""
            : Math.max(0, Number(value))
          : value,
    }));
  };

  /* ===============================
   CATEGORY CHANGE
================================= */
  const handleCategoryChange = async (val) => {
    if (variants.length > 0) {
      const confirm = window.confirm(
        "Changing category may reset some data. Continue?",
      );
      if (!confirm) return;
    }
    setForm((prev) => ({
      ...prev,
      categoryId: val,
      subCategoryId: "",
    }));

    setProductAttributesValues({});
    setVariants([createEmptyVariant()]); // optional but cleaner
    // variants.forEach((v) => v.previews?.forEach((p) => URL.revokeObjectURL(p)));
    // previewImages.forEach((img) => URL.revokeObjectURL(img));
    // setPreviewImages([]);
    // setNewImages([]);

    setSubCategories([]);
    setAttributesMeta({
      productAttributes: [],
      variantAttributes: [],
    });
    setLoadingSub(true);
    await loadSubCategories(val);
    setLoadingSub(false);
  };
  /* ===============================
   SUBCATEGORY CHANGE
================================= */
  const handleSubCategoryChange = async (val) => {
    setForm((prev) => ({
      ...prev,
      subCategoryId: val,
    }));
    setProductAttributesValues({});
    // setVariants([createEmptyVariant()]);
    await loadAttributes(val);
  };

  /* ===============================
   UPDATE VARIANT FIELD
================================= */
  const handleVariantChange = (index, field, value) => {
    setVariants((prev) =>
      prev.map((variant, i) =>
        i === index
          ? {
              ...variant,
              [field]:
                field === "price" ||
                field === "availableQuantity" ||
                field === "weight"
                  ? value === ""
                    ? ""
                    : Math.max(0, Number(value))
                  : value,
            }
          : variant,
      ),
    );
  };

  /* ===============================
   UPDATE VARIANT ATTRIBUTE
================================= */
  const handleVariantAttributeChange = (variantIndex, key, value) => {
    setVariants((prev) =>
      prev.map((variant, i) =>
        i === variantIndex
          ? {
              ...variant,
              attributes: {
                ...(variant.attributes || {}),
                [key]: value,
              },
            }
          : variant,
      ),
    );
  };

  /* ===============================
   ADD NEW VARIANT
================================= */
  const addVariant = () => {
    setVariants((prev) => [...prev, createEmptyVariant()]);
  };

  /* ===============================
   REMOVE VARIANT
================================= */
  const removeVariant = (index) => {
    setVariants((prev) => {
      const variant = prev[index];

      if (variant.id) {
        setRemovedVariantIds((ids) => [...ids, variant.id]);
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  /* ===============================
   PRODUCT IMAGE VALIDATION
================================= */
  const validateImages = (files) => {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    const maxSize = 1 * 1024 * 1024;

    const valid = [];
    const errors = [];

    files.forEach((file) => {
      if (!allowed.includes(file.type)) {
        errors.push(`${file.name} invalid format`);
        return;
      }

      if (file.size > maxSize) {
        errors.push(`${file.name} exceeds 1MB`);
        return;
      }

      valid.push(file);
    });

    return { valid, errors };
  };

  /* ===============================
   ADD PRODUCT IMAGES
================================= */
  const handleNewImages = (e) => {
    const files = Array.from(e.target.files);

    const { valid, errors } = validateImages(files);

    if (errors.length) {
      alert(errors[0]);
      return;
    }

    setNewImages((prev) => [...prev, ...valid]);

    // const previews = valid.map((file) => ({
    //   file,
    //   url: URL.createObjectURL(file),
    // }));
    const previews = valid.map((file) => URL.createObjectURL(file));

    setPreviewImages((prev) => [...prev, ...previews]);
    e.target.value = null; // VERY IMPORTANT, uses for re-selecting the same file
  };

  /* ===============================
   REMOVE NEW IMAGE
================================= */
  const handleRemoveNewImage = (index) => {
    URL.revokeObjectURL(previewImages[index]);

    setNewImages((prev) => prev.filter((_, i) => i !== index));

    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  /* ===============================
   REMOVE EXISTING IMAGE
================================= */
  const handleRemoveExistingImage = (img) => {
    setExistingImages((prev) => {
      const updated = prev.filter((i) => i.id !== img.id);

      if (mainImageId === img.id) {
        setMainImageId(updated[0]?.id || null);
      }

      return updated;
    });

    setRemovedImageIds((prev) => [...prev, img.id]);
  };

  /* ===============================
   SET MAIN IMAGE
================================= */
  const handleSetMainImage = (img) => {
    setMainImageId(img.id);
  };

  /* ===============================
   ADD VARIANT IMAGES
================================= */
  const handleVariantImages = (e, index) => {
    const files = Array.from(e.target.files);
    const { valid } = validateImages(files);

    const previews = valid.map((file) => URL.createObjectURL(file));

    setVariants((prev) =>
      prev.map((variant, i) =>
        i === index
          ? {
              ...variant,
              newImages: [...(variant.newImages || []), ...valid].slice(0, 5),
              previews: [...(variant.previews || []), ...previews].slice(0, 5),
            }
          : variant,
      ),
    );
  };

  /* ===============================
   REMOVE VARIANT IMAGE
================================= */
  const removeVariantImage = (variantIndex, imageIndex, type = "new") => {
    setVariants((prev) =>
      prev.map((variant, i) => {
        if (i !== variantIndex) return variant;

        if (type === "existing") {
          const removed = variant.existingImages[imageIndex];

          return {
            ...variant,
            existingImages: variant.existingImages.filter(
              (_, idx) => idx !== imageIndex,
            ),
            removedImageIds: [...(variant.removedImageIds || []), removed.id],
          };
        }
        if (variant.previews?.[imageIndex]) {
          URL.revokeObjectURL(variant.previews[imageIndex]);
        }

        return {
          ...variant,
          newImages: variant.newImages.filter((_, idx) => idx !== imageIndex),
          previews: variant.previews.filter((_, idx) => idx !== imageIndex),
        };
      }),
    );
  };

  /* ===============================
   SAVE PRODUCT
================================= */
  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);

      const invalidIndex = variants.findIndex(
        (v) => !v.sku?.trim() || v.price === "" || v.availableQuantity === "",
      );

      if (invalidIndex !== -1) {
        alert(`Variant ${invalidIndex + 1} is missing SKU or price`);
        setSaving(false);
        return;
      }

      const sanitizeNumber = (val) =>
        val === "" || val === null ? null : Number(val);

      /* -------------------------
       1. Upload Product Images
    ------------------------- */
      let uploadedProductImages = [];

      if (newImages.length > 0) {
        const fd = new FormData();

        newImages.forEach((file) => fd.append("imageFiles", file));
        if (!product?.sellerId) {
          throw new Error("Missing sellerId");
        }

        fd.append("ownerId", product.sellerId);

        const uploadRes = await fetch(
          `${PRODUCT_API_BASE}/products/upload/images`,
          {
            method: "POST",
            body: fd,
          },
        );

        if (!uploadRes.ok) throw new Error("Image upload failed");

        uploadedProductImages = await uploadRes.json();
      }

      /* -------------------------
       2. Upload Variant Images
    ------------------------- */
      const updatedVariants = await Promise.all(
        variants.map(async (variant) => {
          let uploadedIds = [];

          if (variant.newImages && variant.newImages.length > 0) {
            const fd = new FormData();

            variant.newImages.forEach((file) => {
              fd.append("imageFiles", file);
            });

            fd.append("ownerId", product.sellerId);

            const res = await fetch(
              `${PRODUCT_API_BASE}/products/upload/images`,
              {
                method: "POST",
                body: fd,
              },
            );

            if (!res.ok) throw new Error("Variant image upload failed");

            const uploaded = await res.json();
            uploadedIds = uploaded.map((img) => img.id);
          }

          return {
            id: variant.id,
            sku: variant.sku,
            availableQuantity: sanitizeNumber(variant.availableQuantity),
            weight: sanitizeNumber(variant.weight),
            price: sanitizeNumber(variant.price),

            productCondition: variant.productCondition,
            productStatus: variant.productStatus,
            isDefault: variant.isDefault,

            attributes: variant.attributes,

            imageIds: [
              ...(variant.existingImages || []).map((img) => img.id),
              ...uploadedIds,
            ],

            removedImageIds: variant.removedImageIds || [],
          };
        }),
      );

      let hasDefault = false;

      const sanitizedVariants = updatedVariants.map((v) => {
        if (v.isDefault && !hasDefault) {
          hasDefault = true;
          return v;
        }

        return { ...v, isDefault: false };
      });

      // fallback if none selected
      if (!hasDefault && sanitizedVariants.length > 0) {
        sanitizedVariants[0].isDefault = true;
      }
      /* -------------------------
       3. Build Payload
    ------------------------- */
      const allImageIds = [
        ...existingImages.map((img) => img.id),
        ...uploadedProductImages.map((x) => x.id),
      ];

      const selectedMainImageId =
        allImageIds.find((id) => id === mainImageId) ?? allImageIds[0] ?? null;

      if (!selectedMainImageId) {
        throw new Error("Main image is required");
      }

      const payload = {
        ...form,
        sellerId: product.sellerId,
        attributes: productAttributesValues,

        imageIds: [
          ...existingImages.map((img) => img.id),
          ...uploadedProductImages.map((x) => x.id),
        ],

        mainImageId: selectedMainImageId,

        removedImageIds: removedImageIds,

        // variants: updatedVariants,
        variants: sanitizedVariants,
        removedVariantIds,
      };
      console.log(JSON.stringify(payload, null, 2));

      /* -------------------------
       4. Update Product
    ------------------------- */
      const res = await fetch(`${PRODUCT_API_BASE}/products/seller/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to update product");

      navigate("/dashboard/catalog");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  function CustomSelect({ label, value, options, onChange, disabled }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const selected = (options || []).find((o) => o.id === value);

    //  Close when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div ref={containerRef} className="mb-3 position-relative">
        <label className="form-label">{label}</label>

        {/* Trigger */}
        <div
          className="form-control d-flex justify-content-between align-items-center"
          onClick={() => !disabled && setOpen((prev) => !prev)}
          style={{ cursor: "pointer", background: disabled ? "#eee" : "#fff" }}
        >
          <span>{selected?.name || `Select ${label}`}</span>
          <span>▾</span>
        </div>

        {/* Dropdown */}
        {open && (
          <div
            className="position-absolute w-100 bg-white border rounded shadow-sm"
            style={{
              maxHeight: 200,
              overflowY: "auto",
              zIndex: 9999,
            }}
          >
            {(options || []).map((opt) => (
              <div
                key={opt.id}
                className="px-3 py-2 hover-bg-light"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false); // also close on select
                }}
              >
                {opt.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (loading) return <Spinner />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container-fluid py-3 px-2 px-md-4">
      <h4 className="mb-4 fw-semibold">Edit Product</h4>

      <div className="card shadow-sm p-3 p-md-4">
        {/* ================= CATEGORY ================= */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <CustomSelect
              label="Category"
              value={form.categoryId}
              options={categories}
              onChange={handleCategoryChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <CustomSelect
              label="Subcategory"
              value={form.subCategoryId}
              options={subCategories}
              onChange={handleSubCategoryChange}
              disabled={!form.categoryId || loadingSub}
            />
          </div>
        </div>

        {/* ================= BASIC INFO ================= */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Product Name</label>
            <input
              className="form-control"
              name="productName"
              value={form.productName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Brand</label>
            <input
              className="form-control"
              name="brand"
              value={form.brand}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Model Number</label>
            <input
              className="form-control"
              name="modelNumber"
              value={form.modelNumber}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Currency</label>

            <select
              className="form-select"
              name="currency"
              value={form.currency}
              onChange={handleChange}
            >
              {ALLOWED_CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 mb-3">
            <label className="form-label">Description</label>

            <textarea
              rows={4}
              className="form-control"
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* ================= EXTRA ================= */}
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Warranty Period</label>
            <input
              className="form-control"
              name="warrantyPeriod"
              value={form.warrantyPeriod}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Return Policy</label>
            <input
              className="form-control"
              name="returnPolicy"
              value={form.returnPolicy}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">Dimension Unit</label>

            <select
              className="form-select"
              name="dimensionUnit"
              value={form.dimensionUnit}
              onChange={handleChange}
            >
              {DIMENSION_UNIT.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">Weight Unit</label>

            <select
              className="form-select"
              name="weightUnit"
              value={form.weightUnit}
              onChange={handleChange}
            >
              {WEIGHT_UNIT.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ================= PRODUCT ATTRIBUTES ================= */}

        {attributesMeta.productAttributes?.length > 0 && (
          <div className="card mb-4 shadow-sm border-0">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Product Attributes</h5>

              <div className="row">
                {attributesMeta.productAttributes.map((attr) => (
                  <div key={attr.id} className="col-md-6 mb-3">
                    <label className="form-label">
                      {attr.name}
                      {attr.unit && ` (${attr.unit})`}
                    </label>

                    {attr.inputType === "select" ? (
                      <select
                        value={productAttributesValues[attr.code] || ""}
                        className="form-select"
                        onChange={(e) =>
                          setProductAttributesValues((prev) => ({
                            ...prev,
                            [attr.code]: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select</option>

                        {(attr.options || []).map((opt) => (
                          <option key={opt.id} value={opt.value}>
                            {opt.value}
                          </option>
                        ))}
                      </select>
                    ) : attr.inputType === "checkbox" ? (
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!productAttributesValues[attr.code]}
                        onChange={(e) =>
                          setProductAttributesValues((prev) => ({
                            ...prev,
                            [attr.code]: e.target.checked,
                          }))
                        }
                      />
                    ) : (
                      <input
                        value={productAttributesValues[attr.code] || ""}
                        type={attr.inputType}
                        className="form-control"
                        onChange={(e) =>
                          setProductAttributesValues((prev) => ({
                            ...prev,
                            [attr.code]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= VARIANTS ================= */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Variants</h5>

            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addVariant}
            >
              + Add Variant
            </button>
          </div>

          {variants.map((variant, index) => (
            <div key={index} className="border rounded p-3 mb-4 bg-light">
              <div className="d-flex justify-content-between mb-3">
                <h6>Variant {index + 1}</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={variants.length === 1}
                  onClick={() => removeVariant(index)}
                  // onClick={() =>
                  //   setVariants((prev) => prev.filter((_, i) => i !== index))
                  // }
                >
                  Remove
                </button>
              </div>

              {/* BASIC */}
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={variant.isDefault}
                  onChange={(e) =>
                    setVariants((prev) =>
                      prev.map((v, i) => ({
                        ...v,
                        isDefault: i === index ? e.target.checked : false,
                      })),
                    )
                  }
                />
                <label className="form-check-label">Default Variant</label>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label>SKU</label>
                  <input
                    className="form-control"
                    placeholder="SKU"
                    value={variant.sku}
                    onChange={(e) =>
                      handleVariantChange(index, "sku", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label>Price</label>
                  <input
                    type="number"
                    placeholder="Price"
                    className="form-control"
                    value={variant.price}
                    onChange={(e) =>
                      handleVariantChange(index, "price", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label>Quantity</label>
                  <input
                    type="number"
                    placeholder="Quantity"
                    className="form-control"
                    value={variant.availableQuantity}
                    onChange={(e) =>
                      handleVariantChange(
                        index,
                        "availableQuantity",
                        e.target.value,
                      )
                    }
                  />
                </div>
              </div>

              {/* ATTRIBUTES */}
              <div className="row">
                {(attributesMeta.variantAttributes || []).map((attr) => (
                  <div key={attr.id} className="col-md-4 mb-2">
                    <label className="form-label small">
                      {attr.name}
                      {attr.unit && ` (${attr.unit})`}
                    </label>

                    <select
                      className="form-select"
                      value={variant.attributes?.[attr.code] || ""}
                      onChange={(e) =>
                        handleVariantAttributeChange(
                          index,
                          attr.code,
                          e.target.value,
                        )
                      }
                    >
                      <option value="">Select</option>

                      {(attr.options || []).map((opt) => (
                        <option key={opt.id} value={opt.value}>
                          {opt.value}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {/* IMAGES */}
              <div className="mb-2">
                <label className="form-label">Images</label>

                <input
                  type="file"
                  multiple
                  className="form-control mb-3"
                  onChange={(e) => handleVariantImages(e, index)}
                />

                <div className="d-flex gap-2 flex-wrap">
                  {/* EXISTING IMAGES */}
                  {(variant.existingImages || []).map((img, i) => (
                    <div key={`existing-${i}`} className="position-relative">
                      <img
                        src={`${IMAGE_BASE}${img.storedName}`}
                        width={90}
                        height={90}
                        className="rounded border"
                        style={{ objectFit: "cover" }}
                      />

                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                        onClick={() => removeVariantImage(index, i, "existing")}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {/* NEW IMAGES */}
                  {(variant.previews || []).map((preview, i) => (
                    <div key={`new-${i}`} className="position-relative">
                      <img
                        src={preview}
                        width={90}
                        height={90}
                        className="rounded border"
                        style={{ objectFit: "cover" }}
                      />

                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                        onClick={() => removeVariantImage(index, i, "new")}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ================= PRODUCT IMAGES ================= */}
        <div className="mb-4">
          <h5 className="mb-3">Product Images</h5>

          <input
            type="file"
            multiple
            className="form-control mb-3"
            onChange={handleNewImages}
          />

          <div className="d-flex gap-3 flex-wrap">
            {existingImages.map((img, index) => (
              <div key={index} className="position-relative">
                <img
                  src={`${IMAGE_BASE}${img.storedName}`}
                  width={110}
                  height={110}
                  className="rounded border"
                  onClick={() => handleSetMainImage(img)}
                  style={{
                    border:
                      mainImageId === img.id
                        ? "3px solid #0d6efd"
                        : "1px solid #ddd",
                  }}
                />

                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0"
                  onClick={() => handleRemoveExistingImage(img)}
                >
                  ×
                </button>
              </div>
            ))}

            {previewImages.map((img, index) => (
              <div key={index} className="position-relative">
                <img
                  src={img}
                  width={110}
                  height={110}
                  className="rounded border"
                  style={{
                    objectFit: "cover",
                  }}
                />

                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0"
                  onClick={() => handleRemoveNewImage(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ================= ACTIONS ================= */}
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/dashboard/catalog")}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
